import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loading } from "../components/Loader";
import { apiErrorMessage } from "../shared/apiErrors";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useParams } from "react-router-dom";
import { apiFetch } from "../shared/api";
import {
  fetchPurchaseRequestDetail,
  selectPurchaseRequestById,
  selectPurchaseRequestErrorById,
  selectPurchaseRequestStatusById,
} from "../features/purchaseRequests/purchaseRequestsSlice";
import {
  fetchProductDetail,
  fetchProducts,
  selectProductById,
  selectProducts,
  selectProductsStatus,
} from "../features/products/productsSlice";
import { fetchInventoryByStore } from "../features/inventory/inventorySlice";
import QuickProductCreateModal from "../components/QuickProductCreateModal";
import ProductPricesModal from "../components/ProductPricesModal";
import { selectAuth } from "../features/auth/authSlice";
import { can } from "../features/auth/permissions";
import "./StockTransferDetailPage.css";

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("ru-RU");
}

function formatMoney(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatUser(value) {
  if (!value) return "—";
  if (typeof value === "number" || typeof value === "string") return `#${value}`;
  if (typeof value === "object") {
    const username = value.username ? `@${value.username}` : null;
    const fullName = [value.first_name, value.last_name].filter(Boolean).join(" ").trim() || null;
    const id = value.id != null ? `#${value.id}` : null;
    return [fullName, username, id].filter(Boolean).join(" ") || "—";
  }
  return "—";
}

function purchaseRequestStatusLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "draft") return "Черновик";
  if (s === "in_progress") return "В процессе";
  if (s === "confirmed") return "Подтвержден";
  if (s === "sent") return "Отправлено";
  if (s === "received") return "Получено";
  if (s === "cancelled") return "Отменено";
  return status || "—";
}

// product.cost_price — это розничная цена (легаси-название на бэкенде), product.wholesale_price — оптовая.
function getItemRetailPrice(it, productById) {
  const prod = it?.product != null ? productById[String(it.product)] : null;
  return prod ? prod.cost_price : null;
}
function getItemWholesalePrice(it, productById) {
  const prod = it?.product != null ? productById[String(it.product)] : null;
  return prod ? prod.wholesale_price : null;
}

export default function PurchaseRequestDetailPage() {
  const { requestId } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();

  const fromList = useMemo(() => {
    const from = location.state?.from;
    return typeof from === "string" && from.startsWith("/") ? from : null;
  }, [location.state]);

  const authUser = useSelector(selectAuth).user;
  const request = useSelector(selectPurchaseRequestById(requestId));
  const loadStatus = useSelector(selectPurchaseRequestStatusById(requestId));
  const error = useSelector(selectPurchaseRequestErrorById(requestId));

  const fetchRequest = useCallback(() => {
    dispatch(fetchPurchaseRequestDetail({ requestId }));
  }, [dispatch, requestId]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  const items = useMemo(() => (Array.isArray(request?.items) ? request.items : []), [request?.items]);
  const itemsSorted = useMemo(() => {
    const list = Array.isArray(items) ? [...items] : [];
    return list.sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
  }, [items]);

  // Розничная и оптовая цена берутся из карточки товара, не из позиции заявки.
  useEffect(() => {
    const productIds = [...new Set(items.map((it) => it?.product).filter(Boolean))];
    productIds.forEach((pid) => dispatch(fetchProductDetail({ productId: pid })));
  }, [dispatch, items]);

  const productById = useSelector((state) =>
    Object.fromEntries(
      [...new Set(items.map((it) => it?.product).filter(Boolean))].map((pid) => [
        String(pid),
        selectProductById(pid)(state),
      ])
    )
  );

  const totalRetail = useMemo(
    () =>
      items.reduce((sum, it) => {
        const price = getItemRetailPrice(it, productById);
        return sum + (price != null ? Number(price) * Number(it.quantity || 0) : 0);
      }, 0),
    [items, productById]
  );
  const totalWholesale = useMemo(
    () =>
      items.reduce((sum, it) => {
        const price = getItemWholesalePrice(it, productById);
        return sum + (price != null ? Number(price) * Number(it.quantity || 0) : 0);
      }, 0),
    [items, productById]
  );

  const statusNorm = String(request?.status || "").toLowerCase();
  const isDraft = statusNorm === "draft";
  const isInProgress = statusNorm === "in_progress";
  const isConfirmed = statusNorm === "confirmed";
  const isSent = statusNorm === "sent";
  const canEditItems = isDraft || isInProgress;
  // Директорские поля (себестоимость в любом статусе, розница и опт) — право из матрицы.
  const isDirector = can(authUser, "purchase_request_cost", "write");
  // Цены — поля товара, а не документа, поэтому директор правит их в любом статусе заявки;
  // розничную и оптовую цену правит только он.
  const canEditSelfCost = canEditItems || isDirector;
  const canEditAllPrices = isDirector;

  const [barcode, setBarcode] = useState("");
  const [scanStatus, setScanStatus] = useState("idle");
  const [scanError, setScanError] = useState(null);
  const [lastAddedProductId, setLastAddedProductId] = useState(null);
  // Штрихкод, которого нет в базе — показывает предложение создать товар.
  const [notFoundBarcode, setNotFoundBarcode] = useState(null);
  const [createModalBarcode, setCreateModalBarcode] = useState(null);
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);

  const [deleteStatusById, setDeleteStatusById] = useState(() => ({}));
  const [deleteErrorById, setDeleteErrorById] = useState(() => ({}));

  const [qtyDraftById, setQtyDraftById] = useState(() => ({}));
  const [saveStatusById, setSaveStatusById] = useState(() => ({}));
  const [saveErrorById, setSaveErrorById] = useState(() => ({}));
  const [editingItemId, setEditingItemId] = useState(null);

  // Позиция, для которой открыто окно редактирования цен.
  const [pricesModalItem, setPricesModalItem] = useState(null);

  const [inProgressStatus, setInProgressStatus] = useState("idle");
  const [inProgressError, setInProgressError] = useState(null);
  const [confirmStatus, setConfirmStatus] = useState("idle");
  const [confirmError, setConfirmError] = useState(null);
  const [sendStatus, setSendStatus] = useState("idle");
  const [sendError, setSendError] = useState(null);
  const [receiveStatus, setReceiveStatus] = useState("idle");
  const [receiveError, setReceiveError] = useState(null);
  const [cancelStatus, setCancelStatus] = useState("idle");
  const [cancelError, setCancelError] = useState(null);

  const barcodeNormalized = useMemo(() => String(barcode || "").replace(/\s+/g, "").trim(), [barcode]);
  // Для поиска по названию режем только края: barcodeNormalized убирает все пробелы
  // и склеил бы «кока кола» в «кокакола».
  const searchQueryTrimmed = useMemo(() => String(barcode || "").trim(), [barcode]);
  const isNumericQuery = barcodeNormalized.length > 0 && /^\d+$/.test(barcodeNormalized);
  const barcodeInputRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const qtyInputRefByItemId = useRef({});

  const searchResultsRaw = useSelector(selectProducts);
  const searchStatus = useSelector(selectProductsStatus);
  // Бэкенд не исключает неактивные товары и не даёт по ним фильтра — режем на клиенте.
  const searchResults = useMemo(
    () => (Array.isArray(searchResultsRaw) ? searchResultsRaw : []).filter((p) => p?.is_active !== false),
    [searchResultsRaw]
  );

  const storeId = request?.store ?? null;
  const storeInventory = useSelector((state) =>
    storeId != null ? state.inventory.byStoreId[String(storeId)]?.items : undefined
  );
  const stockByProductId = useMemo(() => {
    const map = {};
    for (const row of Array.isArray(storeInventory) ? storeInventory : []) {
      if (row?.product != null) map[String(row.product)] = row.quantity;
    }
    return map;
  }, [storeInventory]);

  // Расширенный поиск: только цифры — это штрихкод, работает прежний скан.
  // Любой другой ввод ищем по названию и показываем выпадающий список.
  useEffect(() => {
    if (!canEditItems) return;
    if (isNumericQuery || !searchQueryTrimmed) {
      setSearchDropdownOpen(false);
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
      return;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null;
      dispatch(fetchProducts({ search: searchQueryTrimmed, pageSize: 20 }));
      // Остаток в магазине заявки — обогащение строк, поиск идёт по товарам.
      if (storeId != null) {
        dispatch(fetchInventoryByStore({ storeId, search: searchQueryTrimmed, pageSize: 50 }));
      }
      setSearchDropdownOpen(true);
    }, 250);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [canEditItems, dispatch, isNumericQuery, searchQueryTrimmed, storeId]);

  const lookupProductInStore = useCallback(
    async (code) => {
      const c = String(code || "").replace(/\s+/g, "").trim();
      if (!c) throw new Error("barcode is required");
      const data = await apiFetch(`/api/product/products/search-by-barcode/?barcode=${encodeURIComponent(c)}`, {
        method: "GET",
      });
      const product = Array.isArray(data) ? data[0] : Array.isArray(data?.results) ? data.results[0] : data?.product || data;
      if (!product?.id) {
        const e = new Error("Product not found by barcode");
        e.notFound = true;
        e.data = { error: "Товар по штрихкоду не найден" };
        throw e;
      }
      return product;
    },
    []
  );

  const addItemByProductId = useCallback(
    async (productId) => {
      await apiFetch(`/api/product/purchase-requests/${requestId}/items/`, {
        method: "POST",
        body: { product: productId, quantity: 1 },
      });
      setLastAddedProductId(productId);
      fetchRequest();
    },
    [fetchRequest, requestId]
  );

  const onScanAddByBarcode = useCallback(async () => {
    if (!canEditItems) return;
    const c = barcodeNormalized;
    if (!c) return;

    setScanStatus("loading");
    setScanError(null);
    setNotFoundBarcode(null);
    try {
      const product = await lookupProductInStore(c);
      const existing = (items || []).find((x) => String(x?.product) === String(product.id)) || null;
      if (existing?.id) {
        setScanStatus("idle");
        setScanError("Этот товар уже есть в списке — отредактируй количество и нажми «Сохранить».");
        const el = qtyInputRefByItemId.current[String(existing.id)];
        if (el && typeof el.focus === "function") el.focus();
        return;
      }

      setScanStatus("saving");
      await addItemByProductId(product.id);
      setScanStatus("idle");

      if (barcodeInputRef.current && typeof barcodeInputRef.current.select === "function") {
        barcodeInputRef.current.select();
      }
    } catch (err) {
      // 404 — товара нет в базе: предлагаем завести его прямо здесь, а не просто ругаемся.
      if (err?.notFound || err?.status === 404) {
        setScanStatus("idle");
        setScanError("Товар с таким штрихкодом не найден.");
        setNotFoundBarcode(c);
        return;
      }
      const msg = apiErrorMessage(err, "Не удалось добавить товар по штрихкоду");
      setScanStatus("failed");
      setScanError(msg);
    }
  }, [addItemByProductId, barcodeNormalized, canEditItems, items, lookupProductInStore]);

  const addProductFromSearch = useCallback(
    async (product) => {
      if (!canEditItems || !product?.id) return;
      const existing = (items || []).find((x) => String(x?.product) === String(product.id)) || null;

      setSearchDropdownOpen(false);
      setNotFoundBarcode(null);
      if (existing?.id) {
        setBarcode("");
        setScanStatus("idle");
        setScanError("Этот товар уже есть в списке — отредактируй количество и нажми «Сохранить».");
        const el = qtyInputRefByItemId.current[String(existing.id)];
        if (el && typeof el.focus === "function") el.focus();
        return;
      }

      setBarcode("");
      setScanStatus("saving");
      setScanError(null);
      try {
        await addItemByProductId(product.id);
        setScanStatus("idle");
      } catch (err) {
        const msg = apiErrorMessage(err, "Не удалось добавить товар");
        setScanStatus("failed");
        setScanError(msg);
      } finally {
        if (barcodeInputRef.current && typeof barcodeInputRef.current.focus === "function") {
          barcodeInputRef.current.focus();
        }
      }
    },
    [addItemByProductId, canEditItems, items]
  );

  const onCloseCreateModal = useCallback(() => {
    setCreateModalBarcode(null);
    if (barcodeInputRef.current && typeof barcodeInputRef.current.focus === "function") {
      barcodeInputRef.current.focus();
    }
  }, []);

  const onProductCreated = useCallback(
    async (product) => {
      setCreateModalBarcode(null);
      setNotFoundBarcode(null);
      setScanError(null);
      setBarcode("");
      try {
        await addItemByProductId(product?.id);
      } catch (err) {
        const msg = apiErrorMessage(err, "Товар создан, но не удалось добавить его в заявку");
        setScanStatus("failed");
        setScanError(msg);
      }
      if (barcodeInputRef.current && typeof barcodeInputRef.current.focus === "function") {
        barcodeInputRef.current.focus();
      }
    },
    [addItemByProductId]
  );

  const onDeleteItem = useCallback(
    async (itemId) => {
      if (!canEditItems) return;
      const id = String(itemId);
      setDeleteStatusById((p) => ({ ...p, [id]: "saving" }));
      setDeleteErrorById((p) => ({ ...p, [id]: null }));
      try {
        await apiFetch(`/api/product/purchase-requests/${requestId}/items/${itemId}/`, { method: "DELETE" });
        setDeleteStatusById((p) => ({ ...p, [id]: "idle" }));
        fetchRequest();
      } catch (err) {
        const msg = apiErrorMessage(err, "Не удалось удалить позицию");
        setDeleteStatusById((p) => ({ ...p, [id]: "failed" }));
        setDeleteErrorById((p) => ({ ...p, [id]: msg }));
      }
    },
    [canEditItems, fetchRequest, requestId]
  );

  const itemsKey = useMemo(() => (items || []).map((it) => `${it?.id}:${it?.quantity}`).join("|"), [items]);

  useEffect(() => {
    const next = {};
    for (const it of items || []) {
      next[String(it.id)] = it.quantity == null ? "" : String(it.quantity);
    }
    setQtyDraftById(next);
  }, [items, itemsKey]);

  useEffect(() => {
    if (lastAddedProductId == null) return;
    const created = (items || []).find((x) => String(x?.product) === String(lastAddedProductId)) || null;
    if (!created?.id) return;
    const el = qtyInputRefByItemId.current[String(created.id)];
    if (el && typeof el.focus === "function") {
      el.focus();
      if (typeof el.select === "function") el.select();
    }
    setEditingItemId(String(created.id));
    setLastAddedProductId(null);
  }, [items, itemsKey, lastAddedProductId]);

  const onSaveItemQty = useCallback(
    async (itemId) => {
      if (!canEditItems) return;
      const id = String(itemId);
      const raw = String(qtyDraftById[id] ?? "").trim();
      const n = Number(raw);
      if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) {
        setSaveStatusById((p) => ({ ...p, [id]: "failed" }));
        setSaveErrorById((p) => ({ ...p, [id]: "Количество должно быть целым числом > 0" }));
        return;
      }
      setSaveStatusById((p) => ({ ...p, [id]: "saving" }));
      setSaveErrorById((p) => ({ ...p, [id]: null }));
      try {
        await apiFetch(`/api/product/purchase-requests/${requestId}/items/${itemId}/`, {
          method: "PATCH",
          body: { quantity: n },
        });
        setSaveStatusById((p) => ({ ...p, [id]: "idle" }));
        setEditingItemId(null);
        fetchRequest();
      } catch (err) {
        const msg = apiErrorMessage(err, "Не удалось сохранить количество");
        setSaveStatusById((p) => ({ ...p, [id]: "failed" }));
        setSaveErrorById((p) => ({ ...p, [id]: msg }));
      }
    },
    [canEditItems, fetchRequest, qtyDraftById, requestId]
  );

  const onPricesSaved = useCallback(
    (productId) => {
      setPricesModalItem(null);
      fetchRequest();
      // Розничная/оптовая цены берутся из карточки товара — перезапрашиваем её.
      if (productId != null) dispatch(fetchProductDetail({ productId }));
    },
    [dispatch, fetchRequest]
  );

  const callTransition = useCallback(
    async (actionPath, setStatus, setError, fallbackError) => {
      setStatus("saving");
      setError(null);
      try {
        await apiFetch(`/api/product/purchase-requests/${requestId}/${actionPath}/`, { method: "POST" });
        setStatus("idle");
        fetchRequest();
      } catch (err) {
        const msg = apiErrorMessage(err, fallbackError);
        setStatus("failed");
        setError(msg);
      }
    },
    [fetchRequest, requestId]
  );

  const handlePrintPurchaseRequestPdf = useCallback(() => {
    if (!request) return;

    const itemRows = itemsSorted
      .map((it, idx) => {
        const retailPrice = getItemRetailPrice(it, productById);
        const wholesalePrice = getItemWholesalePrice(it, productById);
        return `
      <tr class="${idx % 2 === 0 ? "row-even" : "row-odd"}">
        <td class="td-num">${idx + 1}</td>
        <td class="td-name">${it.product_name || "—"}</td>
        <td class="td-code">${it.product_barcode || "—"}</td>
        <td class="td-right">${it.quantity ?? "—"}</td>
        <td class="td-right">${formatMoney(it.cost_price)}</td>
        <td class="td-right">${formatMoney(it.cost_total)}</td>
        <td class="td-right">${retailPrice != null ? formatMoney(retailPrice) : "—"}</td>
        <td class="td-right td-total">${wholesalePrice != null ? formatMoney(wholesalePrice) : "—"}</td>
      </tr>`;
      })
      .join("");

    const html = `<!DOCTYPE html>
<html lang="ru">
<head>
<meta charset="UTF-8">
<title>Заявка на закуп #${request.id}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #1a1a1a; background: #fff; padding: 20px 28px; }
  .doc-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #2563eb; padding-bottom: 12px; margin-bottom: 16px; }
  .doc-header .left .company { font-size: 18px; font-weight: 700; color: #1e40af; letter-spacing: 0.5px; }
  .doc-header .left .subtitle { font-size: 11px; color: #64748b; margin-top: 2px; }
  .doc-header .right { text-align: right; }
  .doc-header .right .doc-title { font-size: 20px; font-weight: 700; color: #1e40af; }
  .doc-header .right .doc-num { font-size: 13px; color: #64748b; margin-top: 2px; }
  .info-section { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border: 1px solid #e2e8f0; border-radius: 4px; margin-bottom: 18px; overflow: hidden; }
  .info-block { padding: 10px 14px; background: #f8fafc; }
  .info-block:nth-child(even) { background: #fff; border-left: 1px solid #e2e8f0; }
  .info-row { display: flex; align-items: baseline; gap: 8px; margin-bottom: 5px; }
  .info-row:last-child { margin-bottom: 0; }
  .info-label { font-size: 10px; color: #64748b; min-width: 110px; text-transform: uppercase; letter-spacing: 0.4px; }
  .info-value { font-size: 11.5px; font-weight: 600; color: #1e293b; }
  .section-title { font-size: 11px; font-weight: 700; color: #1e40af; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 6px; margin-top: 18px; padding-bottom: 4px; border-bottom: 1px solid #bfdbfe; }
  table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
  th { background: #1e40af; color: #fff; padding: 6px 8px; text-align: left; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; white-space: nowrap; }
  td { padding: 5px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: middle; }
  .row-even { background: #fff; }
  .row-odd { background: #f8fafc; }
  .td-right { text-align: right; white-space: nowrap; }
  .td-num { text-align: center; color: #94a3b8; width: 28px; }
  .td-code { color: #64748b; font-family: monospace; font-size: 9.5px; }
  .td-name { font-weight: 500; }
  .td-total { font-weight: 700; color: #1e293b; }
  .totals-wrap { display: flex; justify-content: flex-end; margin-top: 10px; }
  .totals-table { min-width: 260px; }
  .totals-table td { border: none; padding: 3px 8px; }
  .totals-table .tot-label { color: #64748b; text-align: right; font-size: 10.5px; }
  .totals-table .tot-val { text-align: right; font-weight: 600; font-size: 11px; min-width: 90px; }
  .totals-table .grand-row td { border-top: 2px solid #1e40af; padding-top: 6px; font-size: 13px; font-weight: 700; color: #1e40af; }
  .doc-footer { margin-top: 28px; border-top: 1px solid #e2e8f0; padding-top: 8px; display: flex; justify-content: space-between; color: #94a3b8; font-size: 9.5px; }
  @media print {
    body { padding: 0; }
    @page { margin: 14mm 12mm; }
  }
</style>
</head>
<body>
  <div class="doc-header">
    <div class="left">
      <div class="company">АУФ</div>
      <div class="subtitle">Заявка на закуп</div>
    </div>
    <div class="right">
      <div class="doc-title">Заявка</div>
      <div class="doc-num">№ ${request.id} от ${formatDateTime(request.created_at)}</div>
    </div>
  </div>

  <div class="info-section">
    <div class="info-block">
      <div class="info-row"><span class="info-label">Название</span><span class="info-value">${request.name || "—"}</span></div>
      <div class="info-row"><span class="info-label">Склад</span><span class="info-value">${request.store_name || `Store #${request.store}` || "—"}</span></div>
    </div>
    <div class="info-block">
      <div class="info-row"><span class="info-label">Статус</span><span class="info-value">${purchaseRequestStatusLabel(request.status)}</span></div>
      <div class="info-row"><span class="info-label">Подтверждено</span><span class="info-value">${formatDateTime(request.confirmed_at)}</span></div>
      <div class="info-row"><span class="info-label">Отправлено</span><span class="info-value">${formatDateTime(request.sent_at)}</span></div>
      <div class="info-row"><span class="info-label">Получено</span><span class="info-value">${formatDateTime(request.received_at)}</span></div>
    </div>
  </div>

  <div class="section-title">Позиции</div>
  <table>
    <thead>
      <tr>
        <th class="td-num">#</th>
        <th>Наименование</th>
        <th>Штрих-код</th>
        <th class="td-right">Кол-во</th>
        <th class="td-right">Себест. за ед.</th>
        <th class="td-right">Сумма по себест.</th>
        <th class="td-right">Розничная цена</th>
        <th class="td-right">Оптовая цена</th>
      </tr>
    </thead>
    <tbody>${itemRows || `<tr><td colspan="8" style="text-align:center;color:#94a3b8;padding:10px">Нет позиций</td></tr>`}</tbody>
  </table>

  <div class="totals-wrap">
    <table class="totals-table">
      <tbody>
        <tr><td class="tot-label">Итого по себестоимости</td><td class="tot-val">${formatMoney(request.total_cost)}</td></tr>
        <tr><td class="tot-label">Итого по розничной цене</td><td class="tot-val">${formatMoney(totalRetail)}</td></tr>
        <tr class="grand-row"><td class="tot-label">Итого по оптовой цене</td><td class="tot-val">${formatMoney(totalWholesale)}</td></tr>
      </tbody>
    </table>
  </div>

  ${request.comment ? `<div class="notes-box" style="background:#fefce8;border:1px solid #fef08a;border-radius:4px;padding:8px 12px;font-size:10.5px;color:#713f12;margin-top:14px"><strong>Комментарий:</strong> ${request.comment}</div>` : ""}

  <div class="doc-footer">
    <span>Сформировано: ${new Date().toLocaleString("ru-RU")}</span>
    <span>АУФ — Заявка на закуп #${request.id}</span>
  </div>

  <script>
    window.onload = function() {
      window.focus();
      window.print();
    };
  </script>
</body>
</html>`;

    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) {
      window.alert("Разрешите всплывающие окна для печати PDF");
      return;
    }
    w.document.write(html);
    w.document.close();
  }, [request, itemsSorted, productById, totalRetail, totalWholesale]);

  return (
    <div className="stock-transfer-detail-page">
      <div className="header">
        <div>
          <div className="breadcrumbs">
            <Link to={fromList || "/warehouse/purchase-requests"} className="link">
              Запросы на закуп
            </Link>
            <span className="sep">/</span>
            <span className="muted">Заявка #{requestId}</span>
          </div>
          <div className="title">Заявка #{requestId}</div>
          <div className="sub-title">
            Детали заявки и список позиций (items) из <span className="mono">/api/product/purchase-requests/{requestId}/</span>.
          </div>
        </div>

        <div className="header-actions">
          {(isDraft || isInProgress) && (
            <Link to={`/warehouse/purchase-requests/${requestId}/edit`} state={{ from: fromList }} className="btn-ghost-link">
              Редактировать
            </Link>
          )}
          {isDraft && (
            <button
              type="button"
              onClick={() =>
                callTransition("to-in-progress", setInProgressStatus, setInProgressError, "Не удалось перевести в работу")
              }
              className="btn-primary"
              disabled={inProgressStatus === "saving"}
            >
              {inProgressStatus === "saving" ? "Перевожу…" : "В работу"}
            </button>
          )}
          {isInProgress && (
            <button
              type="button"
              onClick={() => callTransition("confirm", setConfirmStatus, setConfirmError, "Не удалось подтвердить")}
              className="btn-primary"
              disabled={confirmStatus === "saving"}
            >
              {confirmStatus === "saving" ? "Подтверждаю…" : "Подтвердить"}
            </button>
          )}
          {isConfirmed && (
            <button
              type="button"
              onClick={() => callTransition("send", setSendStatus, setSendError, "Не удалось отправить")}
              className="btn-primary"
              disabled={sendStatus === "saving"}
            >
              {sendStatus === "saving" ? "Отправляю…" : "Отправить"}
            </button>
          )}
          {isSent && (
            <button
              type="button"
              onClick={() => callTransition("receive", setReceiveStatus, setReceiveError, "Не удалось отметить как получено")}
              className="btn-success"
              disabled={receiveStatus === "saving"}
            >
              {receiveStatus === "saving" ? "Принимаю…" : "Получено"}
            </button>
          )}
          {statusNorm !== "received" && statusNorm !== "cancelled" && (
            <button
              type="button"
              onClick={() => callTransition("cancel", setCancelStatus, setCancelError, "Не удалось отменить")}
              className="btn-danger-large"
              disabled={cancelStatus === "saving"}
            >
              {cancelStatus === "saving" ? "Отменяю…" : "Отменить"}
            </button>
          )}
          <button type="button" onClick={fetchRequest} className="btn">
            Обновить
          </button>
          <button
            type="button"
            onClick={handlePrintPurchaseRequestPdf}
            className="btn"
            disabled={!request}
            title="Печать / сохранить как PDF"
          >
            🖨 PDF
          </button>
        </div>
      </div>

      {loadStatus === "loading" && <Loading text="Загрузка…" />}
      {loadStatus === "failed" && <div className="error">{error}</div>}
      {inProgressStatus === "failed" && inProgressError && <div className="error">{inProgressError}</div>}
      {confirmStatus === "failed" && confirmError && <div className="error">{confirmError}</div>}
      {sendStatus === "failed" && sendError && <div className="error">{sendError}</div>}
      {receiveStatus === "failed" && receiveError && <div className="error">{receiveError}</div>}
      {cancelStatus === "failed" && cancelError && <div className="error">{cancelError}</div>}

      {request && (
        <>
          <div className="meta">
            <div className="meta-row">
              <div className="meta-label">ID</div>
              <div className="meta-value-mono">{request.id ?? "—"}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Название</div>
              <div className="meta-value">{request.name || "—"}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Склад</div>
              <div className="meta-value">{request.store_name || `Store #${request.store}` || "—"}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Статус</div>
              <div className="meta-value">{purchaseRequestStatusLabel(request.status)}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Комментарий</div>
              <div className="meta-value">{request.comment || "—"}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Создал</div>
              <div className="meta-value">{formatUser(request.created_by)}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Взял в работу</div>
              <div className="meta-value">{formatUser(request.in_progress_by)}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Подтвердил</div>
              <div className="meta-value">{formatUser(request.confirmed_by)}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Отправил</div>
              <div className="meta-value">{formatUser(request.sent_by)}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Получил</div>
              <div className="meta-value">{formatUser(request.received_by)}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Отменил</div>
              <div className="meta-value">{formatUser(request.cancelled_by)}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">В работе</div>
              <div className="meta-value">{formatDateTime(request.in_progress_at)}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Подтверждено</div>
              <div className="meta-value">{formatDateTime(request.confirmed_at)}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Отправлено</div>
              <div className="meta-value">{formatDateTime(request.sent_at)}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Получено</div>
              <div className="meta-value">{formatDateTime(request.received_at)}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Создано</div>
              <div className="meta-value">{formatDateTime(request.created_at)}</div>
            </div>
            <div className="meta-row">
              <div className="meta-label">Обновлено</div>
              <div className="meta-value">{formatDateTime(request.updated_at)}</div>
            </div>
          </div>

          <div className="body">
            <div className="section-title">Позиции: {items.length}</div>

            {canEditItems && (
              <div className="add-card">
                <div className="add-title">Добавить позицию</div>
                <div className="add-sub-title">
                  Отсканируй штрихкод или начни вводить название — товары появятся списком.
                </div>
                <div className="add-row">
                  <div className="search-field-wrap">
                    <input
                      ref={barcodeInputRef}
                      value={barcode}
                      onChange={(e) => setBarcode(e.target.value)}
                      onFocus={() => {
                        if (!isNumericQuery && searchQueryTrimmed && searchResults.length > 0) {
                          setSearchDropdownOpen(true);
                        }
                      }}
                      onBlur={() => {
                        window.setTimeout(() => setSearchDropdownOpen(false), 150);
                      }}
                      placeholder="Штрихкод или название товара…"
                      className="big-input"
                      autoComplete="off"
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          setSearchDropdownOpen(false);
                          return;
                        }
                        if (e.key !== "Enter") return;
                        e.preventDefault();
                        if (searchDropdownOpen && !isNumericQuery && searchResults.length > 0) {
                          addProductFromSearch(searchResults[0]);
                          return;
                        }
                        onScanAddByBarcode();
                      }}
                    />
                    {searchDropdownOpen && !isNumericQuery && (
                      <div className="search-dropdown">
                        {searchStatus === "loading" && <div className="search-dropdown-empty">Ищу…</div>}
                        {searchStatus !== "loading" && searchResults.length === 0 && (
                          <div className="search-dropdown-empty">Ничего не найдено</div>
                        )}
                        {searchResults.length > 0 && (
                          <div className="search-dropdown-section">
                            <div className="search-dropdown-section-title">Товары</div>
                            {searchResults.map((p) => (
                              <button
                                type="button"
                                key={p.id}
                                className="search-dropdown-item"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => addProductFromSearch(p)}
                              >
                                <span className="search-dropdown-name">{p.name}</span>
                                <span className="search-dropdown-meta">
                                  {p.barcode || "—"} · Остаток: {stockByProductId[String(p.id)] ?? 0}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={onScanAddByBarcode}
                    className="btn-primary"
                    disabled={!barcodeNormalized || scanStatus === "loading" || scanStatus === "saving"}
                  >
                    {scanStatus === "saving" ? "Добавляю…" : scanStatus === "loading" ? "Поиск…" : "Добавить"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setBarcode("");
                      setScanStatus("idle");
                      setScanError(null);
                      setNotFoundBarcode(null);
                      setSearchDropdownOpen(false);
                      if (barcodeInputRef.current) barcodeInputRef.current.focus();
                    }}
                    className="btn-ghost"
                  >
                    Очистить
                  </button>
                </div>
                {scanError && <div className="error-inline">{scanError}</div>}
                {notFoundBarcode && (
                  <div className="add-row" style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => setCreateModalBarcode(notFoundBarcode)}
                    >
                      + Создать товар «{notFoundBarcode}»
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th className="th">ID</th>
                    <th className="th">Product</th>
                    <th className="th">Название</th>
                    <th className="th">Штрихкод</th>
                    <th className="th">Количество</th>
                    <th className="th">Себест. за ед.</th>
                    <th className="th">Сумма по себест.</th>
                    <th className="th">Розничная цена</th>
                    <th className="th">Оптовая цена</th>
                    <th className="th">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="td-muted">
                        Нет позиций
                      </td>
                    </tr>
                  ) : (
                    itemsSorted.map((it) => {
                      const retailPrice = getItemRetailPrice(it, productById);
                      const wholesalePrice = getItemWholesalePrice(it, productById);
                      return (
                      <tr key={it.id}>
                        <td className="td-mono">{it.id}</td>
                        <td className="td-mono">{it.product ?? "—"}</td>
                        <td className="td">{it.product_name || "—"}</td>
                        <td className="td-mono">{it.product_barcode || "—"}</td>
                        <td
                          className="td"
                          onDoubleClick={() => {
                            if (!canEditItems) return;
                            setEditingItemId(String(it.id));
                            window.setTimeout(() => {
                              const el = qtyInputRefByItemId.current[String(it.id)];
                              if (el && typeof el.focus === "function") {
                                el.focus();
                                if (typeof el.select === "function") el.select();
                              }
                            }, 0);
                          }}
                          title={canEditItems ? "Двойной клик — редактировать количество" : undefined}
                        >
                          {canEditItems && String(editingItemId) === String(it.id) ? (
                            <input
                              ref={(el) => {
                                if (el) qtyInputRefByItemId.current[String(it.id)] = el;
                              }}
                              value={qtyDraftById[String(it.id)] ?? ""}
                              onChange={(e) => {
                                const v = e.target.value;
                                setQtyDraftById((p) => ({ ...p, [String(it.id)]: v }));
                                setSaveErrorById((p) => ({ ...p, [String(it.id)]: null }));
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") onSaveItemQty(it.id);
                                if (e.key === "Escape") setEditingItemId(null);
                              }}
                              className="qty-input-cell"
                              inputMode="numeric"
                              type="number"
                              step="1"
                              min="1"
                            />
                          ) : (
                            <span className={canEditItems ? "qty-readonly-hint" : undefined}>{it.quantity ?? "—"}</span>
                          )}
                        </td>
                        <td className="td">{formatMoney(it.cost_price)}</td>
                        <td className="td">{formatMoney(it.cost_total)}</td>
                        <td className="td">{retailPrice != null ? formatMoney(retailPrice) : "—"}</td>
                        <td className="td">{wholesalePrice != null ? formatMoney(wholesalePrice) : "—"}</td>
                        <td className="td">
                          {canEditItems && String(editingItemId) === String(it.id) && (
                            <button
                              type="button"
                              onClick={() => onSaveItemQty(it.id)}
                              className="btn-small-primary"
                              disabled={!canEditItems || saveStatusById[String(it.id)] === "saving"}
                            >
                              {saveStatusById[String(it.id)] === "saving" ? "Сохраняю…" : "Сохранить"}
                            </button>
                          )}
                          {(canEditSelfCost || canEditAllPrices) && (
                            <button
                              type="button"
                              onClick={() => setPricesModalItem(it)}
                              className="btn-small-primary"
                              title="Редактировать цены товара и посмотреть историю изменений"
                            >
                              Цены
                            </button>
                          )}
                          {canEditItems && (
                            <>
                              <button
                                type="button"
                                onClick={() => onDeleteItem(it.id)}
                                className="btn-danger"
                                disabled={deleteStatusById[String(it.id)] === "saving"}
                              >
                                {deleteStatusById[String(it.id)] === "saving" ? "Удаляю…" : "Удалить"}
                              </button>
                              {deleteErrorById[String(it.id)] && <div className="error-mini">{deleteErrorById[String(it.id)]}</div>}
                            </>
                          )}
                          {canEditItems && saveErrorById[String(it.id)] && <div className="error-mini">{saveErrorById[String(it.id)]}</div>}
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
                {items.length > 0 && (
                  <tfoot>
                    <tr>
                      <td className="td" colSpan={8} style={{ textAlign: "right", fontWeight: 600 }}>
                        Итого по себестоимости
                      </td>
                      <td className="td" style={{ fontWeight: 600 }}>{formatMoney(request.total_cost)}</td>
                      <td className="td" />
                    </tr>
                    <tr>
                      <td className="td" colSpan={8} style={{ textAlign: "right", fontWeight: 600 }}>
                        Итого по розничной цене
                      </td>
                      <td className="td" style={{ fontWeight: 600 }}>{formatMoney(totalRetail)}</td>
                      <td className="td" />
                    </tr>
                    <tr>
                      <td className="td" colSpan={8} style={{ textAlign: "right", fontWeight: 600 }}>
                        Итого по оптовой цене
                      </td>
                      <td className="td" style={{ fontWeight: 600 }}>{formatMoney(totalWholesale)}</td>
                      <td className="td" />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}

      <QuickProductCreateModal
        open={canEditItems && createModalBarcode != null}
        initialBarcode={createModalBarcode}
        onCancel={onCloseCreateModal}
        onCreated={onProductCreated}
      />

      <ProductPricesModal
        open={pricesModalItem != null}
        product={pricesModalItem?.product != null ? productById[String(pricesModalItem.product)] : null}
        itemId={pricesModalItem?.id}
        requestId={requestId}
        canEditSelfCost={canEditSelfCost}
        canEditAllPrices={canEditAllPrices}
        onCancel={() => setPricesModalItem(null)}
        onSaved={onPricesSaved}
      />
    </div>
  );
}
